import { mm } from "@inovitas/infra3dsdk";
import { IFeatureStyle } from "@inovitas/infra3dsdk/dist/types/mm/featuretree/interfaces/IFeatureStyle";
import { FeatureCollection } from "geojson";
import React from "react";

import { FeatureColumn } from "shared/ui/toolbar/tools/layertreeAndEdit/featureTable/FeatureListTable";

export interface Sorter {
  property: string;
  direction: "ASC" | "DESC";
}

export interface IUnsavedLayerChanges {
  title: string;
  topic: string;
  geometrytype: mm.GEOMETRYTYPE;
  propertydef: {
    add: mm.LayerPropertyDef[];
    remove: string[];
    rename: { oldkey: string; newkey: string }[];
    applied: mm.LayerPropertyDef[];
  };
  style: IFeatureStyle;
}

export const getGeometryTypeEnum = (
  name: string | mm.GEOMETRYTYPE | undefined
): number => {
  switch (name) {
    case mm.GEOMETRYTYPE.LINESTRING:
    case "linestring":
    case "LineString":
      return mm.GEOMETRYTYPE.LINESTRING;
    case mm.GEOMETRYTYPE.MULTILINESTRING:
    case "multilinestring":
    case "MultiLineString":
      return mm.GEOMETRYTYPE.MULTILINESTRING;
    case mm.GEOMETRYTYPE.POLYGON:
    case "polygon":
    case "Polygon":
      return mm.GEOMETRYTYPE.POLYGON;
    case mm.GEOMETRYTYPE.MULTIPOLYGON:
    case "multipolygon":
    case "MultiPolygon":
      return mm.GEOMETRYTYPE.MULTIPOLYGON;
    case mm.GEOMETRYTYPE.POINT:
    case "point":
    case "Point":
      return mm.GEOMETRYTYPE.POINT;
    case mm.GEOMETRYTYPE.MULTIPOINT:
    case "multipoint":
    case "MultiPoint":
      return mm.GEOMETRYTYPE.MULTIPOINT;
    default:
      return mm.GEOMETRYTYPE.UNDEFINED;
  }
};

export const getFeaturesCountFromLayer = (layer: mm.Layer): Promise<number> => {
  return new Promise((resolve, reject) => {
    layer.exportFeatures(
      (count) => {
        resolve(count);
      },
      (error: any) => {
        reject(error);
      },
      {
        countonly: true,
      }
    );
  });
};

export const getFeatureCollectionFromLayer = (
  layer: mm.Layer,
  start?: number,
  limit?: number,
  sorters?: Sorter[],
  srid?: number
): Promise<FeatureCollection> => {
  return new Promise((resolve, reject) => {
    layer.exportFeatures(
      (featureCollection: FeatureCollection) => {
        resolve(featureCollection);
      },
      (error: any) => {
        reject(error);
      },
      {
        start: start || 0,
        limit: limit || undefined,
        srid: srid || layer.scene.getCurrentGeoframe().srid,
        sorters: sorters || [],
      }
    );
  });
};

export const getColumnDefinitionsFromLayer = (
  layer: mm.Layer
): FeatureColumn[] => {
  const columns: FeatureColumn[] = [];
  columns.push({
    id: 0,
    key: "id",
    name: "ID",
    datatype: mm.LAYERPROPERTYTYPE.INTEGER,
    editable: false,
  });
  if (layer.propertydef.length > 0) {
    layer.propertydef.forEach((item, index: number) => {
      if (item.key !== "id") {
        columns.push({
          id: index + 1,
          ...item,
        });
      }
    });
  }
  return columns;
};

export const defaultPointStyle: IFeatureStyle = {
  stylename: "layerstyle",
  rules: [
    {
      filter: "",
      symbolizer: "point",
      params: {
        size: 20,
        stroke: [0.05, 0.57, 0.82, 1],
        "font-marker": {
          name: "circle",
          rotation: 0,
          fontsize: 20,
          anchor: ["center", "middle"],
        },
      },
    },
  ],
};

export const defaultLineStyle: IFeatureStyle = {
  stylename: "layerstyle",
  rules: [
    {
      filter: "",
      symbolizer: "point",
      params: {
        size: 20,
        stroke: [0.05, 0.57, 0.82, 1],
        "font-marker": {
          name: "circle",
          rotation: 0,
          fontsize: 20,
          anchor: ["center", "middle"],
        },
      },
    },
    {
      filter: "",
      symbolizer: "line",
      params: {
        "stroke-width": 4,
        stroke: [0.05, 0.57, 0.82, 1],
      },
    },
  ],
};

export const defaultPolygonStyle: IFeatureStyle = {
  stylename: "layerstyle",
  rules: [
    {
      filter: "",
      symbolizer: "point",
      params: {
        size: 20,
        stroke: [0.05, 0.57, 0.82, 1],
        "font-marker": {
          name: "circle",
          rotation: 0,
          fontsize: 20,
          anchor: ["center", "middle"],
        },
      },
    },
    {
      filter: "",
      symbolizer: "line",
      params: {
        "stroke-width": 4,
        stroke: [0.05, 0.57, 0.82, 1],
      },
    },
    {
      filter: "",
      symbolizer: "polygon",
      params: {
        fill: [0.77, 0.86, 0.91, 0.4],
      },
    },
  ],
};
